import React, { useState } from 'react';
import {
  Sparkles,
  Zap,
  Rocket,
  Star,
  Megaphone,
  Bell,
  Flame,
  Gift,
  Compass,
  Layers,
  TrendingUp,
  BarChart2,
  DollarSign,
  Calendar,
  Clock,
  CreditCard,
  Users,
  UserCheck,
  HeartHandshake,
  Mail,
  Send,
  MessageSquare,
  Phone,
  ShieldCheck,
  ShieldAlert,
  Lock,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Info,
  HelpCircle,
  Search,
  Database,
  Workflow,
  Sliders,
  Eye,
  RefreshCw,
  FileText,
  Settings,
  Wrench,
  Truck,
  Package,
  Box,
  MapPin,
  Globe,
  Tag,
  Award,
  Bookmark,
  Flag,
  Lightbulb,
  Cpu,
  Check,
  ChevronDown,
  X
} from 'lucide-react';

export interface IconDefinition {
  name: string;
  label: string;
  category: 'Highlights' | 'Analytics & Logistics' | 'Communication & Team' | 'Status & Security' | 'System & Tools';
  component: React.ComponentType<{ className?: string }>;
}

export const ICON_REGISTRY: Record<string, IconDefinition> = {
  // Highlights & Features
  Sparkles: { name: 'Sparkles', label: 'Sparkles', category: 'Highlights', component: Sparkles },
  Zap: { name: 'Zap', label: 'Lightning / Fast', category: 'Highlights', component: Zap },
  Rocket: { name: 'Rocket', label: 'Rocket / Launch', category: 'Highlights', component: Rocket },
  Star: { name: 'Star', label: 'Star / Featured', category: 'Highlights', component: Star },
  Flame: { name: 'Flame', label: 'Flame / Hot', category: 'Highlights', component: Flame },
  Gift: { name: 'Gift', label: 'Gift / Special', category: 'Highlights', component: Gift },
  Award: { name: 'Award', label: 'Award / Quality', category: 'Highlights', component: Award },
  Lightbulb: { name: 'Lightbulb', label: 'Lightbulb / Idea', category: 'Highlights', component: Lightbulb },
  Megaphone: { name: 'Megaphone', label: 'Megaphone / Announcement', category: 'Highlights', component: Megaphone },
  Tag: { name: 'Tag', label: 'Tag / Badge', category: 'Highlights', component: Tag },

  // Analytics, Logistics & Finance
  TrendingUp: { name: 'TrendingUp', label: 'Trending Up', category: 'Analytics & Logistics', component: TrendingUp },
  BarChart2: { name: 'BarChart2', label: 'Bar Chart / Analytics', category: 'Analytics & Logistics', component: BarChart2 },
  DollarSign: { name: 'DollarSign', label: 'Dollar / Rates / Revenue', category: 'Analytics & Logistics', component: DollarSign },
  CreditCard: { name: 'CreditCard', label: 'Credit Card / Billing', category: 'Analytics & Logistics', component: CreditCard },
  Truck: { name: 'Truck', label: 'Truck / Freight Delivery', category: 'Analytics & Logistics', component: Truck },
  Package: { name: 'Package', label: 'Package / Freight Parcel', category: 'Analytics & Logistics', component: Package },
  Box: { name: 'Box', label: 'Box / Pallet Container', category: 'Analytics & Logistics', component: Box },
  Calendar: { name: 'Calendar', label: 'Calendar / Deadlines', category: 'Analytics & Logistics', component: Calendar },
  Clock: { name: 'Clock', label: 'Clock / Scheduled Follow-Up', category: 'Analytics & Logistics', component: Clock },
  MapPin: { name: 'MapPin', label: 'Map Pin / Warehouse Location', category: 'Analytics & Logistics', component: MapPin },
  Globe: { name: 'Globe', label: 'Globe / Network', category: 'Analytics & Logistics', component: Globe },

  // Communication & Team
  Users: { name: 'Users', label: 'Users / Team / Contacts', category: 'Communication & Team', component: Users },
  UserCheck: { name: 'UserCheck', label: 'User Check / Assigned Rep', category: 'Communication & Team', component: UserCheck },
  HeartHandshake: { name: 'HeartHandshake', label: 'Handshake / Partnership', category: 'Communication & Team', component: HeartHandshake },
  Mail: { name: 'Mail', label: 'Mail / Gmail Outreach', category: 'Communication & Team', component: Mail },
  Send: { name: 'Send', label: 'Send / Outbound Message', category: 'Communication & Team', component: Send },
  MessageSquare: { name: 'MessageSquare', label: 'Chat / Activity Log', category: 'Communication & Team', component: MessageSquare },
  Phone: { name: 'Phone', label: 'Phone / Call Touchpoint', category: 'Communication & Team', component: Phone },
  Bell: { name: 'Bell', label: 'Bell / Notification Alert', category: 'Communication & Team', component: Bell },

  // Status & Security
  ShieldCheck: { name: 'ShieldCheck', label: 'Shield Check / Secure', category: 'Status & Security', component: ShieldCheck },
  ShieldAlert: { name: 'ShieldAlert', label: 'Shield Alert / Admin Only', category: 'Status & Security', component: ShieldAlert },
  Lock: { name: 'Lock', label: 'Lock / Permissions', category: 'Status & Security', component: Lock },
  CheckCircle: { name: 'CheckCircle', label: 'Check Circle / Success', category: 'Status & Security', component: CheckCircle },
  AlertTriangle: { name: 'AlertTriangle', label: 'Alert Triangle / Warning', category: 'Status & Security', component: AlertTriangle },
  AlertCircle: { name: 'AlertCircle', label: 'Alert Circle / Urgent', category: 'Status & Security', component: AlertCircle },
  Info: { name: 'Info', label: 'Info / Guidance', category: 'Status & Security', component: Info },
  HelpCircle: { name: 'HelpCircle', label: 'Help Circle / Support Guide', category: 'Status & Security', component: HelpCircle },
  Flag: { name: 'Flag', label: 'Flag / Milestone', category: 'Status & Security', component: Flag },
  Bookmark: { name: 'Bookmark', label: 'Bookmark / Saved', category: 'Status & Security', component: Bookmark },

  // System & Tools
  Workflow: { name: 'Workflow', label: 'Workflow / Pipeline Engine', category: 'System & Tools', component: Workflow },
  Sliders: { name: 'Sliders', label: 'Sliders / Custom Filters', category: 'System & Tools', component: Sliders },
  Database: { name: 'Database', label: 'Database / CData Sync', category: 'System & Tools', component: Database },
  Search: { name: 'Search', label: 'Search / Shipper Lookup', category: 'System & Tools', component: Search },
  Eye: { name: 'Eye', label: 'Eye / Smart Views', category: 'System & Tools', component: Eye },
  RefreshCw: { name: 'RefreshCw', label: 'Refresh / Real-time Sync', category: 'System & Tools', component: RefreshCw },
  FileText: { name: 'FileText', label: 'File Text / Template Editor', category: 'System & Tools', component: FileText },
  Settings: { name: 'Settings', label: 'Settings / Maintenance', category: 'System & Tools', component: Settings },
  Wrench: { name: 'Wrench', label: 'Wrench / Tools', category: 'System & Tools', component: Wrench },
  Layers: { name: 'Layers', label: 'Layers / Non-Floral Modules', category: 'System & Tools', component: Layers },
  Compass: { name: 'Compass', label: 'Compass / Routing', category: 'System & Tools', component: Compass },
  Cpu: { name: 'Cpu', label: 'AI / Automated Logic', category: 'System & Tools', component: Cpu },
};

export const AVAILABLE_ICON_NAMES = Object.keys(ICON_REGISTRY);

export interface DynamicIconProps {
  name?: string;
  className?: string;
  fallback?: React.ComponentType<{ className?: string }>;
}

/**
 * Renders a Lucide icon safely by string name with case-insensitive fallback.
 */
export function DynamicIcon({
  name,
  className = 'w-5 h-5',
  fallback: Fallback = Sparkles
}: DynamicIconProps) {
  if (!name || typeof name !== 'string') {
    return <Fallback className={className} />;
  }

  // Exact match
  if (ICON_REGISTRY[name]) {
    const Component = ICON_REGISTRY[name].component;
    return <Component className={className} />;
  }

  // Case-insensitive match
  const lower = name.toLowerCase().trim();
  const matchedKey = Object.keys(ICON_REGISTRY).find(k => k.toLowerCase() === lower);
  if (matchedKey) {
    const Component = ICON_REGISTRY[matchedKey].component;
    return <Component className={className} />;
  }

  // Fallback
  return <Fallback className={className} />;
}

export interface IconPickerProps {
  selectedIcon: string;
  onSelect: (iconName: string) => void;
  className?: string;
}

/**
 * Rich interactive icon selector with category filtering and instant search.
 */
export function IconPicker({
  selectedIcon,
  onSelect,
  className = ''
}: IconPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', 'Highlights', 'Analytics & Logistics', 'Communication & Team', 'Status & Security', 'System & Tools'];

  const filteredIcons = Object.values(ICON_REGISTRY).filter((item) => {
    const matchesSearch =
      !search ||
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.label.toLowerCase().includes(search.toLowerCase()) ||
      item.category.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 px-3 py-2 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-bold text-slate-800 dark:text-slate-200 transition cursor-pointer shadow-sm"
      >
        <div className="w-6 h-6 rounded-md bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
          <DynamicIcon name={selectedIcon} className="w-4 h-4" />
        </div>
        <span className="font-mono text-[11px]">{selectedIcon || 'Select Icon'}</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 ml-auto" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute left-0 mt-2 w-80 md:w-96 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl z-50 p-3 flex flex-col gap-2.5 animate-in fade-in zoom-in-95 duration-100">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 text-xs font-extrabold text-slate-900 dark:text-slate-100">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <span>Select Feature Icon</span>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-900 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search 30+ icons (e.g. Zap, Truck, Rate, Mail)..."
                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg pl-8 pr-3 py-1.5 text-xs text-slate-800 dark:text-slate-200 outline-none focus:border-blue-500 font-medium"
                autoFocus
              />
            </div>

            {/* Category Filter Pills */}
            <div className="flex items-center gap-1 overflow-x-auto pb-1 text-[10px] font-bold no-scrollbar">
              {categories.map((cat) => (
                <button
                  type="button"
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-2 py-0.5 rounded-full whitespace-nowrap transition cursor-pointer ${
                    selectedCategory === cat
                      ? 'bg-blue-600 text-white shadow-xs'
                      : 'bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Icon Grid */}
            <div className="max-h-60 overflow-y-auto grid grid-cols-4 sm:grid-cols-5 gap-1.5 p-1 border border-slate-100 dark:border-slate-900 rounded-lg bg-slate-50/50 dark:bg-slate-900/30">
              {filteredIcons.length === 0 ? (
                <div className="col-span-full py-8 text-center text-xs text-slate-400">
                  No matching icons found.
                </div>
              ) : (
                filteredIcons.map((item) => {
                  const isSelected = selectedIcon === item.name;
                  const IconComp = item.component;
                  return (
                    <button
                      type="button"
                      key={item.name}
                      onClick={() => {
                        onSelect(item.name);
                        setIsOpen(false);
                      }}
                      title={item.label}
                      className={`flex flex-col items-center justify-center p-2 rounded-lg border transition cursor-pointer group ${
                        isSelected
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md ring-2 ring-blue-400/30'
                          : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-950/30'
                      }`}
                    >
                      <IconComp className={`w-5 h-5 ${isSelected ? 'text-white' : 'group-hover:scale-110 transition-transform'}`} />
                      <span className="text-[9px] mt-1 truncate max-w-full font-mono">{item.name}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DynamicIcon;
